import { useState, type ClipboardEvent, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { splitTags, tagsFromValue } from "./tagList";

// A list of short values (MAC addresses, broadcast names) edited as removable
// chips. The value it reads and writes is the same newline-joined string the
// config drafts already use for string_array fields, so it drops in where a
// "one per line" textarea used to be.

export interface TagListInputProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  // Optional check per chip; a non-null result marks the chip as invalid.
  invalid?: (tag: string) => string | null;
  monospace?: boolean;
}

export function TagListInput({ id, value, onChange, placeholder, maxLength, invalid, monospace = true }: TagListInputProps) {
  const [draft, setDraft] = useState("");
  const tags = tagsFromValue(value);

  function commit(text: string) {
    const incoming = splitTags(text);
    if (incoming.length === 0) return;
    const seen = new Set(tags.map((t) => t.toUpperCase()));
    const added = incoming.filter((t) => {
      const key = t.toUpperCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    if (added.length > 0) onChange([...tags, ...added].join("\n"));
    setDraft("");
  }

  function remove(index: number) {
    onChange(tags.filter((_, i) => i !== index).join("\n"));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && tags.length > 0) {
      remove(tags.length - 1);
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData("text");
    if (/[\s,;]/.test(text.trim())) {
      e.preventDefault();
      commit(`${draft} ${text}`);
    }
  }

  return (
    <div className="flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:border-riot-gold-500 focus-within:ring-1 focus-within:ring-riot-gold-500 dark:border-riot-navy-600 dark:bg-riot-navy-950">
      {tags.map((tag, index) => {
        const problem = invalid ? invalid(tag) : null;
        return (
          <span
            key={`${tag}-${index}`}
            title={problem ?? undefined}
            className={
              problem
                ? "inline-flex items-center gap-1 rounded border border-red-300 bg-red-50 py-0.5 pl-2 pr-1 text-xs text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                : "inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-100 py-0.5 pl-2 pr-1 text-xs text-gray-800 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-riot-navy-100"
            }
          >
            <span className={monospace ? "font-mono" : undefined}>{tag}</span>
            <button
              type="button"
              onClick={() => remove(index)}
              aria-label={`Remove ${tag}`}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-gray-500 hover:bg-gray-200 hover:text-red-600 dark:text-riot-navy-300 dark:hover:bg-riot-navy-700 dark:hover:text-red-300"
            >
              <X size={12} />
            </button>
          </span>
        );
      })}
      <input
        id={id}
        type="text"
        value={draft}
        maxLength={maxLength}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        onBlur={() => commit(draft)}
        placeholder={tags.length === 0 ? placeholder : "Add another…"}
        className={`min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm focus:outline-none focus:ring-0 dark:text-white ${monospace ? "font-mono" : ""}`}
      />
    </div>
  );
}
